import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  BiCreditCard,
  BiCrown,
  BiImageAdd,
  BiReceipt,
  BiPlus,
  BiQr,
  BiStore,
} from "react-icons/bi";
import { useToast } from "../../../components/Toast/ToastContext";
import type { TaxOption } from "../../../services/taxApi";
import {
  PaymentFee,
  PaymentMethod,
  SquareStatus,
  addPaymentMethod,
  addTax,
  apiErrorMessage,
  deletePaymentMethod,
  deleteTax,
  disconnectSquare,
  getPaymentMethods,
  getSquareConnectUrl,
  getSquareStatus,
  getTaxes,
  setSquareEmployeeAccess,
  updatePaymentMethod,
  updateTax,
} from "../../../services/settingsApi";
import {
  Card,
  ChoiceChips,
  EmptyNote,
  Field,
  Modal,
  Toggle,
  dangerButtonClass,
  inputClass,
  linkButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../settingsUi";
import { currencySymbol, formatMoney } from "../../../services/currency";

const TIER_LABEL: Record<string, string> = {
  none: "Free",
  lite: "Lite",
  pro: "Pro",
  elite: "Elite",
};

const MAX_QR_BYTES = 2 * 1024 * 1024;

const isCardLike = (name?: string | null) => /(card|credit|debit)/i.test(name || "");

const formatFeeLabel = (fee?: PaymentFee) => {
  if (!fee?.enabled || fee.type === "none") return null;
  const value = Number(fee.value || 0);
  const fixed = Number(fee.fixedAmount || 0);
  if (fee.type === "percent") {
    return fixed > 0 ? `${value}% + ${formatMoney(fixed)} fee` : `${value}% fee`;
  }
  return `${formatMoney(value)} fee`;
};

/** "percentFixed" is saved as a percent fee with a fixed amount (2.6% + $0.15). */
type CardFeeChoice = "percent" | "flat" | "percentFixed";

const FEE_TYPE_OPTIONS: Array<{ value: CardFeeChoice; label: string }> = [
  { value: "percent", label: "Percent" },
  { value: "percentFixed", label: "Percent + fixed" },
  { value: "flat", label: "Flat amount" },
];

const feeExample = (type: CardFeeChoice, raw: string, fixedRaw: string) => {
  const value = Number(raw || 0);
  const fixed = type === "percentFixed" ? Number(fixedRaw || 0) : 0;
  if (!Number.isFinite(value) || value < 0 || !Number.isFinite(fixed)) return null;
  const fee = type === "flat" ? value : value + fixed;
  if (fee <= 0) return null;
  return `A ${formatMoney(100)} card sale becomes ${formatMoney(100 + fee)}.`;
};

// ── Subscription ───────────────────────────────────────────────────────────

const SubscriptionCard = ({ tier }: { tier: string }) => (
  <Card
    icon={BiCrown}
    title="Subscription"
    description={`You're on the ${TIER_LABEL[tier] || "Free"} plan.`}
    action={
      <Link to="/billing" className={secondaryButtonClass}>
        Manage plan
      </Link>
    }
  />
);

// ── Square ─────────────────────────────────────────────────────────────────

const SquareCard = ({ readOnly }: { readOnly: boolean }) => {
  const { notify } = useToast();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<SquareStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await getSquareStatus());
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Back from Square: ?square=connected|failed.
  const squareResult = params.get("square");
  useEffect(() => {
    if (!squareResult) return;
    if (squareResult === "connected") {
      notify("Square connected.", { type: "success" });
    } else {
      notify(
        params.get("reason") === "missing_code_state"
          ? "Square wasn't connected. You can try again any time."
          : "Couldn't connect Square. Please try again.",
        { type: "error" }
      );
    }
    const next = new URLSearchParams(params);
    next.delete("square");
    next.delete("reason");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squareResult]);

  const connect = async () => {
    setBusy(true);
    try {
      const url = await getSquareConnectUrl();
      if (!url) throw new Error("missing url");
      // Square sends the owner back to this page when they're done.
      window.location.assign(url);
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't open Square."), { type: "error" });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Square? Card payments through Square stop until you reconnect.")) {
      return;
    }
    setBusy(true);
    try {
      await disconnectSquare();
      notify("Square disconnected.", { type: "success" });
      await load();
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't disconnect Square."), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const toggleEmployeeAccess = async (allowed: boolean) => {
    const previous = status;
    setStatus((s) => (s ? { ...s, employeeAccess: allowed } : s));
    try {
      await setSquareEmployeeAccess(allowed);
      notify(
        allowed
          ? "Employees can use your Square while working your events."
          : "Square is owner-only. Employees can still take cash and QR payments.",
        { type: "success" }
      );
    } catch (error) {
      setStatus(previous);
      notify(apiErrorMessage(error, "Couldn't update Square."), { type: "error" });
    }
  };

  const connected = !!status?.connected;
  const detail = connected
    ? [status?.merchantName, status?.locationName].filter(Boolean).join(" · ") || "Connected"
    : readOnly
      ? status?.message || "Your employer's Square isn't available to you right now."
      : "Take card payments with Square at your events.";

  return (
    <Card
      icon={BiStore}
      title="Square"
      description={
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden
            className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`}
          />
          {status ? detail : "Checking…"}
        </span>
      }
      action={
        !readOnly &&
        status &&
        (connected ? (
          <button type="button" className={secondaryButtonClass} onClick={disconnect} disabled={busy}>
            Disconnect
          </button>
        ) : (
          <button type="button" className={primaryButtonClass} onClick={connect} disabled={busy}>
            Connect Square
          </button>
        ))
      }
    >
      {connected && !readOnly && (
        <Toggle
          label="Employees can use your Square"
          description="Only while they work your events."
          checked={!!status?.employeeAccess}
          onChange={toggleEmployeeAccess}
        />
      )}
      <p className={`text-sm text-slate-500 ${connected && !readOnly ? "mt-4" : ""}`}>
        Card readers and Tap to Pay are set up in the VendorOrbis mobile app.
      </p>
    </Card>
  );
};

// ── Card fee ───────────────────────────────────────────────────────────────

const CardFeeCard = ({
  cardMethod,
  readOnly,
  onSaved,
}: {
  cardMethod: PaymentMethod | null;
  readOnly: boolean;
  onSaved: () => void;
}) => {
  const { notify } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [type, setType] = useState<CardFeeChoice>("percent");
  const [value, setValue] = useState("");
  const [fixed, setFixed] = useState("");
  const [taxable, setTaxable] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fee = cardMethod?.fee;
    setEnabled(!!fee?.enabled);
    setType(
      fee?.type === "flat" ? "flat" : Number(fee?.fixedAmount) > 0 ? "percentFixed" : "percent"
    );
    setValue(fee?.value != null && !Number.isNaN(Number(fee.value)) ? String(fee.value) : "");
    setFixed(Number(fee?.fixedAmount) > 0 ? String(fee?.fixedAmount) : "");
    setTaxable(fee?.taxable === true);
  }, [cardMethod]);

  const example = enabled ? feeExample(type, value, fixed) : null;

  const save = async () => {
    const fee: PaymentFee = enabled
      ? {
          type: type === "flat" ? "flat" : "percent",
          value: Number(value) || 0,
          fixedAmount: type === "percentFixed" ? Number(fixed) || 0 : 0,
          enabled: true,
          label: "Card processing fee",
          taxable,
        }
      : { type: "none", value: 0, enabled: false, taxable: false };
    setSaving(true);
    try {
      if (cardMethod) await updatePaymentMethod(cardMethod._id, "Card", null, fee);
      else await addPaymentMethod("Card", null, fee);
      notify("Card fee saved.", { type: "success" });
      onSaved();
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't save the card fee."), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (readOnly) {
    return (
      <Card
        icon={BiCreditCard}
        title="Card processing fee"
        description={formatFeeLabel(cardMethod?.fee) || "No card fee is added at checkout."}
      />
    );
  }

  return (
    <Card
      icon={BiCreditCard}
      title="Card processing fee"
      description="Pass card processing costs on to customers who pay by card."
    >
      <div className="space-y-4">
        <Toggle label="Add a fee to card payments" checked={enabled} onChange={setEnabled} />
        {enabled && (
          <>
            <ChoiceChips options={FEE_TYPE_OPTIONS} value={type} onChange={setType} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={type === "flat" ? `Fee amount (${currencySymbol()})` : "Fee (%)"}>
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={type === "flat" ? "0.50" : "2.6"}
                />
              </Field>
              {type === "percentFixed" && (
                <Field label={`Plus per sale (${currencySymbol()})`}>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={fixed}
                    onChange={(e) => setFixed(e.target.value)}
                    placeholder="0.15"
                  />
                </Field>
              )}
            </div>
            <Toggle
              label="Charge sales tax on the fee"
              checked={taxable}
              onChange={setTaxable}
            />
            {example && <p className="text-sm text-slate-500">{example}</p>}
          </>
        )}
        <button type="button" className={primaryButtonClass} onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save card fee"}
        </button>
      </div>
    </Card>
  );
};

// ── Payment methods ────────────────────────────────────────────────────────

const PaymentMethodModal = ({
  open,
  method,
  onClose,
}: {
  open: boolean;
  /** null: adding a new one. */
  method: PaymentMethod | null;
  onClose: (changed?: boolean) => void;
}) => {
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(method?.paymentMethod ?? "");
    setFile(null);
    setPreview(method?.qrCode || null);
  }, [open, method]);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (picked?: File) => {
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      notify("Choose an image file.", { type: "error" });
      return;
    }
    if (picked.size > MAX_QR_BYTES) {
      notify("That image is over 2 MB. Choose a smaller one.", { type: "error" });
      return;
    }
    setFile(picked);
  };

  const card = isCardLike(name);
  const canSave = !!name.trim() && (!!method || !!file || card);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      if (method) await updatePaymentMethod(method._id, name.trim(), file, method.fee);
      else await addPaymentMethod(name.trim(), file);
      notify(method ? "Payment method updated." : "Payment method added.", { type: "success" });
      onClose(true);
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't save the payment method."), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={method ? "Edit payment method" : "Add payment method"}
      onClose={() => onClose()}
      footer={
        <>
          <button type="button" className={secondaryButtonClass} onClick={() => onClose()}>
            Cancel
          </button>
          <button
            type="submit"
            form="payment-method-form"
            className={primaryButtonClass}
            disabled={!canSave || saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form id="payment-method-form" onSubmit={save} className="space-y-4">
        <Field label="Name" hint="For example Venmo, Cash App, or Zelle.">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            required
          />
        </Field>
        {!card && (
          <div>
            <p className="text-sm font-medium text-slate-700">QR code</p>
            <p className="text-xs text-slate-500">
              Customers scan it at checkout. PNG or JPG, up to 2 MB.
            </p>
            <label className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-5 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600">
              {preview ? (
                <img src={preview} alt="QR code" className="h-40 w-40 rounded-lg object-contain" />
              ) : (
                <BiImageAdd size={32} />
              )}
              <span className="font-semibold">{preview ? "Replace image" : "Upload image"}</span>
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </label>
          </div>
        )}
      </form>
    </Modal>
  );
};

const PaymentMethodsCard = ({
  methods,
  readOnly,
  ownerName,
  onChanged,
}: {
  methods: PaymentMethod[];
  readOnly: boolean;
  ownerName?: string;
  onChanged: () => void;
}) => {
  const { notify } = useToast();
  const [editing, setEditing] = useState<PaymentMethod | null>(null);
  const [open, setOpen] = useState(false);

  const remove = async (method: PaymentMethod) => {
    if (!window.confirm(`Delete ${method.paymentMethod}?`)) return;
    try {
      await deletePaymentMethod(method._id);
      notify("Payment method deleted.", { type: "success" });
      onChanged();
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't delete the payment method."), { type: "error" });
    }
  };

  return (
    <Card
      icon={BiQr}
      title="Payment methods"
      description={
        readOnly
          ? `${ownerName || "Your employer"}'s payment methods, used at checkout.`
          : "Ways customers can pay you, shown at checkout."
      }
      action={
        !readOnly && (
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <BiPlus size={18} /> Add
          </button>
        )
      }
    >
      {methods.length === 0 ? (
        <EmptyNote>No payment methods yet.</EmptyNote>
      ) : (
        <ul className="divide-y divide-slate-100">
          {methods.map((method) => (
            <li key={method._id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
              {method.qrCode ? (
                <img
                  src={method.qrCode}
                  alt=""
                  className="h-12 w-12 flex-shrink-0 rounded-lg border border-slate-200 object-contain"
                />
              ) : (
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  {isCardLike(method.paymentMethod) ? <BiCreditCard size={22} /> : <BiQr size={22} />}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{method.paymentMethod}</p>
                {formatFeeLabel(method.fee) && (
                  <p className="text-sm text-slate-500">{formatFeeLabel(method.fee)}</p>
                )}
              </div>
              {!readOnly && (
                <div className="flex gap-3">
                  <button
                    type="button"
                    className={linkButtonClass}
                    onClick={() => {
                      setEditing(method);
                      setOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`${linkButtonClass} text-red-600 hover:text-red-700`}
                    onClick={() => remove(method)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <PaymentMethodModal
        open={open}
        method={editing}
        onClose={(changed) => {
          setOpen(false);
          if (changed) onChanged();
        }}
      />
    </Card>
  );
};

// ── Tax rates ──────────────────────────────────────────────────────────────

const TaxRatesCard = ({ readOnly, ownerName }: { readOnly: boolean; ownerName?: string }) => {
  const { notify } = useToast();
  const [taxes, setTaxes] = useState<TaxOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: "", rate: "" });
  const [adding, setAdding] = useState({ name: "", rate: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setTaxes(await getTaxes(readOnly));
    } catch {
      notify("Couldn't load tax rates.", { type: "error" });
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const parseRate = (raw: string) => {
    const rate = parseFloat(raw);
    return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : null;
  };

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      notify(success, { type: "success" });
      await load();
      return true;
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't save the tax rate."), { type: "error" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const rate = parseRate(adding.rate);
    if (!adding.name.trim() || rate === null) {
      notify("Enter a name and a rate between 0 and 100.", { type: "error" });
      return;
    }
    const ok = await run(
      () => addTax({ name: adding.name.trim(), rate, isDefault: taxes.length === 0 }),
      "Tax rate added."
    );
    if (ok) setAdding({ name: "", rate: "" });
  };

  const saveEdit = async (id: string) => {
    const rate = parseRate(draft.rate);
    if (!draft.name.trim() || rate === null) {
      notify("Enter a name and a rate between 0 and 100.", { type: "error" });
      return;
    }
    if (await run(() => updateTax(id, { name: draft.name.trim(), rate }), "Tax rate updated.")) {
      setEditingId(null);
    }
  };

  return (
    <Card
      icon={BiReceipt}
      title="Tax rates"
      description={
        readOnly
          ? `${ownerName || "Your employer"}'s sales tax rates.`
          : "Sales tax rates you can apply at checkout. The default one is picked automatically."
      }
    >
      {!loaded ? (
        <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
      ) : taxes.length === 0 ? (
        <EmptyNote>
          {readOnly ? `${ownerName || "Your employer"} hasn't added tax rates.` : "No tax rates yet."}
        </EmptyNote>
      ) : (
        <ul className="divide-y divide-slate-100">
          {taxes.map((tax) =>
            editingId === tax._id ? (
              <li key={tax._id} className="flex flex-wrap items-end gap-2 py-3 first:pt-0">
                <input
                  className={`${inputClass} mt-0 flex-1`}
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  aria-label="Tax name"
                />
                <input
                  className={`${inputClass} mt-0 w-24`}
                  inputMode="decimal"
                  value={draft.rate}
                  onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))}
                  aria-label="Rate (%)"
                />
                <button
                  type="button"
                  className={primaryButtonClass}
                  onClick={() => saveEdit(tax._id)}
                  disabled={busy}
                >
                  Save
                </button>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </button>
              </li>
            ) : (
              <li key={tax._id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">
                    {tax.name}
                    {tax.isDefault && (
                      <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        Default
                      </span>
                    )}
                  </p>
                </div>
                <span className="font-semibold tabular-nums text-slate-700">
                  {Number(tax.rate).toFixed(2)}%
                </span>
                {!readOnly && (
                  <div className="flex gap-3">
                    {!tax.isDefault && (
                      <button
                        type="button"
                        className={linkButtonClass}
                        disabled={busy}
                        onClick={() =>
                          run(() => updateTax(tax._id, { isDefault: true }), `${tax.name} is now the default.`)
                        }
                      >
                        Make default
                      </button>
                    )}
                    <button
                      type="button"
                      className={linkButtonClass}
                      onClick={() => {
                        setEditingId(tax._id);
                        setDraft({ name: tax.name, rate: String(tax.rate) });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`${linkButtonClass} text-red-600 hover:text-red-700`}
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(`Delete ${tax.name}?`)) return;
                        run(() => deleteTax(tax._id), "Tax rate deleted.");
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}

      {!readOnly && (
        <form onSubmit={add} className="mt-5 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-5">
          <div className="min-w-[10rem] flex-1">
            <Field label="Name">
              <input
                className={inputClass}
                value={adding.name}
                onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
                placeholder="State sales tax"
              />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Rate (%)">
              <input
                className={inputClass}
                inputMode="decimal"
                value={adding.rate}
                onChange={(e) => setAdding((a) => ({ ...a, rate: e.target.value }))}
                placeholder="8.25"
              />
            </Field>
          </div>
          <button type="submit" className={secondaryButtonClass} disabled={busy}>
            <BiPlus size={18} /> Add rate
          </button>
        </form>
      )}
    </Card>
  );
};

// ── Section ────────────────────────────────────────────────────────────────

type Props = {
  tier: string;
  /** Working in an employer's business: their settings, read-only. */
  workingForEmployer: boolean;
  employerName?: string;
};

const BillingSection = ({ tier, workingForEmployer, employerName }: Props) => {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);

  const loadMethods = useCallback(async () => {
    try {
      setMethods(await getPaymentMethods(workingForEmployer));
    } catch {
      setMethods([]);
    }
  }, [workingForEmployer]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const cardMethod = useMemo(
    () => methods.find((m) => isCardLike(m.paymentMethod)) ?? null,
    [methods]
  );

  return (
    <div className="space-y-5">
      {workingForEmployer ? (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          You're working in {employerName || "your employer"}'s business. These are their payment
          settings; only they can change them.
        </p>
      ) : (
        <SubscriptionCard tier={tier} />
      )}
      <SquareCard readOnly={workingForEmployer} />
      <CardFeeCard cardMethod={cardMethod} readOnly={workingForEmployer} onSaved={loadMethods} />
      <PaymentMethodsCard
        methods={methods}
        readOnly={workingForEmployer}
        ownerName={employerName}
        onChanged={loadMethods}
      />
      <TaxRatesCard readOnly={workingForEmployer} ownerName={employerName} />
    </div>
  );
};

export default BillingSection;
